import _, { startCase } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { apiUrl } from "features/configure";
import axios from "axios";
import { useOutletContext } from "react-router";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useIntl } from "react-intl";

// 初始entity有哪些權限，並且顯示在organization role 上方權限選單
export default function useGetRolesTable() {
  const [header, setHeader] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [entityName] = useOutletContext();
  const dispatchSnackbar = useDispatchSnackbar();
  const { formatMessage } = useIntl();
  const [roleList, setRoleList] = useState([]);

  // 使用 useMemo 記住 permissionOrder
  const fetchRoles = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${apiUrl}/api/role/${entityName}`);
      // 建立 header 排序參考
      const permissionOrder = header.reduce((acc, category) => {
        category.children.forEach((item) => {
          acc.push(item.value);
        });
        return acc;
      }, []);

      const sortedRoleList = data.map((item) => {
        item.permissions = item.permissions.sort((a, b) => {
          const indexA = permissionOrder.indexOf(a.name);
          const indexB = permissionOrder.indexOf(b.name);
          return indexA - indexB;
        });

        return item;
      });
      setRoleList(sortedRoleList);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (header.length === 0) return;
    fetchRoles();
  }, [header]);

  useEffect(() => {
    async function getHeader() {
      try {
        setIsLoading(true);
        const { data } = await axios.get(
          apiUrl + `/api/role/${entityName}/features`
        );
        const headerGrouped = _.groupBy(data, "category");
        const sortedCategories = [
          "Services",
          "Notification",
          "Analytics",
          "KOL",
          "Customers",
          "Settings",
          "Account",
        ];

        const disableRead = ["integration"];
        const disableEdit = ["team"];
        const headerNameMapping = {
          webTraffic: formatMessage({ id: "global.function.websiteTraffic" }),
          profileCompany: formatMessage({
            id: "global.function.companySetting",
          }),
          profileUser: formatMessage({ id: "global.function.profile" }),
          onchainData: formatMessage({
            id: "global.function.dataSourceSetting",
          }),
          aiManagement: formatMessage({
            id: "global.function.aiSupport",
          }),
        };

        const categoryMapping = {
          Services: formatMessage({ id: "global.module.community" }),
          Notification: formatMessage({ id: "global.module.notification" }),
          Analytics: formatMessage({ id: "global.module.footprints" }),
          KOL: formatMessage({ id: "global.module.kol" }),
          Customers: formatMessage({ id: "global.module.customers" }),
          Settings: formatMessage({ id: "global.module.settings" }),
          Account: formatMessage({ id: "global.module.account" }),
        };

        // Sort categories according to the predefined order
        let headerArr = [];
        sortedCategories.forEach((category) => {
          if (headerGrouped[category]) {
            headerArr.push({
              title: categoryMapping[category] || category,
              children: headerGrouped[category].map((item) => {
                const disabledOptions = [];
                if (item.onlyAdmin || disableEdit.includes(item.name))
                  disabledOptions.push("edit");
                if (disableRead.includes(item.name))
                  disabledOptions.push("read");
                return {
                  title: Object.keys(headerNameMapping).includes(item.name)
                    ? headerNameMapping[item.name]
                    : formatMessage({ id: `global.function.${item.name}` }),
                  value: item.name,
                  disabled: disabledOptions,
                };
              }),
            });
          }
        });
        setHeader((prev) => [...headerArr]);
      } catch (err) {
        console.log(err);
        dispatchSnackbar({ err });
      } finally {
        setIsLoading(false);
      }
    }
    getHeader();
  }, []);

  return { header, isLoading, body: roleList, fetchBody: fetchRoles };
}
